import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import useAuth from "../hooks/useAuth";
import { getOptionsDirection } from "../utils/hasCodeChars";

/* =========================
   CONFIG
========================= */
const CAMERA_GRACE_PERIOD = 15; // seconds before auto-submit

const Assessment = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finalTime, setFinalTime] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [examEndDate, setExamEndDate] = useState(null);
  const [examStartDate, setExamStartDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [cameraOk, setCameraOk] = useState(false);
  const [cameraLost, setCameraLost] = useState(false);
  const [cameraLostTime, setCameraLostTime] = useState(0);
  const [cameraStream, setCameraStream] = useState(null);

  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [loadingAssessment, setLoadingAssessment] = useState(true);

  const videoRef = useRef(null);

  const selectedQuestions = useMemo(() => {
    if (!questions.length) return [];
    return questions.map((q) => ({
      ...q,
      options: q.options || [],
    }));
  }, [questions]);

  const currentQuestion = selectedQuestions[currentIndex];

  /* =========================
     FETCH QUESTIONS
  ========================== */
  const fetchQuestions = async () => {
    const storedEndDate = localStorage.getItem("assessment_end_date");
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/start-assessment/${courseId}`, {
        headers: { Authorization: `Bearer ${user?.token}`, lang: i18n.language },
      });

      const allQuestions = res.data.data.questions || [];
      const unsolvedQuestions = allQuestions.filter((q) => q.solution === null || q.solution === undefined);

      setQuestions(unsolvedQuestions);
      const normalizedDate = storedEndDate.replace(" ", "T");

      const examEnd = new Date(normalizedDate);
      const examStart = new Date(res.data.data.exam_started_at || Date.now());
      setExamEndDate(examEnd);
      setExamStartDate(examStart);

      const remaining = Math.floor((examEnd.getTime() - Date.now()) / 1000);
      setTimeLeft(remaining > 0 ? remaining : 0);
      setLoadingAssessment(false);
      setStarted(true);
    } catch (err) {
      console.error("Failed to load assessment", err);
      if (err.response?.status === 404) navigate("/", { replace: true });
      if (err.response?.status === 401) navigate("/auth/login", { replace: true });
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!questions.length) return;
    setCurrentIndex(0);
  }, [questions]);

  /* =========================
     SUBMIT / FINISH
  ========================== */
  const finishAssessment = async () => {
    if (finished || submitting) return;

    setSubmitting(true); // ✅ START LOADER
    setFinished(true);

    if (examStartDate) {
      setFinalTime(Math.floor((Date.now() - examStartDate.getTime()) / 1000));
    }

    if (cameraStream) cameraStream.getTracks().forEach((t) => t.stop());
    if (mediaRecorder && mediaRecorder.state !== "inactive") mediaRecorder.stop();

    try {
      const blob = new Blob(recordedChunks, { type: "video/webm" });
      const formData = new FormData();
      formData.append("video", blob, "assessment_record.webm");

      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/finish/${courseId}`, formData, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });

      setFinalResult(res.data.data); // ✅ RESULT RECEIVED
    } catch (err) {
      console.error("Failed to finish assessment", err.response?.data || err);
    } finally {
      setSubmitting(false); // ✅ STOP LOADER
    }
  };

  const submitAssessment = async () => {
    await finishAssessment();
  };

  /* =========================
     CAMERA & RECORDING
  ========================== */
  useEffect(() => {
    if (!started || finished) return;

    const setupCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        setCameraStream(stream);
        setCameraOk(true);
        setCameraLost(false);
        setCameraLostTime(0);

        const recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) setRecordedChunks((prev) => [...prev, e.data]);
        };
        recorder.start(1000);
        setMediaRecorder(recorder);
      } catch (err) {
        console.error("Camera access denied:", err);
        setCameraOk(false);
        setCameraLost(true);
      } finally {
        setLoadingAssessment(false);
      }
    };

    setupCamera();

    return () => {
      if (cameraStream) cameraStream.getTracks().forEach((t) => t.stop());
    };
  }, [started, finished]);

  useEffect(() => {
    if (!videoRef.current || !cameraStream) return;
    videoRef.current.srcObject = cameraStream;
    videoRef.current.play().catch(() => {});
  }, [cameraStream]);

  // Camera monitoring
  useEffect(() => {
    if (!started || finished) return;

    const interval = setInterval(() => {
      const videoTracks = cameraStream?.getVideoTracks() || [];
      const activeTrack = videoTracks[0];

      if (!activeTrack || activeTrack.readyState === "ended") {
        setCameraLost(true);
        setCameraLostTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= CAMERA_GRACE_PERIOD) submitAssessment();
          return newTime;
        });
      } else {
        setCameraLost(false);
        setCameraLostTime(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [started, cameraStream, finished]);

  /* =========================
     TIMER
  ========================== */
  useEffect(() => {
    if (!examEndDate || finished) return;

    const updateTimer = () => {
      const remaining = Math.floor((examEndDate.getTime() - Date.now()) / 1000);
      setTimeLeft(remaining > 0 ? remaining : 0);
      if (remaining <= 0) submitAssessment();
    };

    updateTimer(); // immediate update
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [examEndDate, finished]);

  /* =========================
     ANSWERS
  ========================== */

  const submitAnswer = async (question, selectedOption) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/answer/${courseId}`,
        {
          question_id: question.id,
          answer_id: selectedOption.id,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
    } catch (err) {
      console.error("Failed to submit answer", err.response?.data || err);
    }
  };

  const selectAnswer = async (selectedIndex) => {
    if (finished || cameraLost || !currentQuestion) return;

    const selectedOption = currentQuestion.options[selectedIndex];
    if (!selectedOption) return;

    // Save locally
    const newAnswers = [...answers];
    newAnswers[currentIndex] = {
      questionId: currentQuestion.id,
      selectedIndex,
    };
    setAnswers(newAnswers);

    // ✅ SEND to backend
    await submitAnswer(currentQuestion, selectedOption);
  };

  /* =========================
     RESULTS DATA
  ========================== */
  const score = finalResult?.correct_answers ?? 0;
  const total = finalResult?.total_questions ?? selectedQuestions.length;
  const completionTimeSec =
    finalTime ?? (examStartDate ? Math.floor((Date.now() - examStartDate.getTime()) / 1000) : 0);
  const cameraCountdown = CAMERA_GRACE_PERIOD - cameraLostTime;
  const minutes = Math.floor(completionTimeSec / 60);
  const seconds = completionTimeSec % 60;
  const completionTimeFormatted = `${minutes}:${String(seconds).padStart(2, "0")}`;

  const totalDuration = examStartDate && examEndDate ? Math.floor((examEndDate - examStartDate) / 1000) : 1;

  /* =========================
     UI
  ========================== */
  if (loadingAssessment)
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
        <svg
          className="h-10 w-10 animate-spin text-purple-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            className="opacity-25"
          ></circle>
          <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
      </div>
    );

  if (!cameraOk)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-red-100 px-6 text-center">
        <h2 className="mb-4 text-2xl font-bold text-red-700">{t("camera_required")}</h2>
        <p className="mb-4 text-red-600">{t("camera_lost_message")}</p>
      </div>
    );

  if (!selectedQuestions.length && !loadingAssessment) {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-50 px-4">
        <div className="max-w-md rounded-3xl bg-white p-12 text-center shadow-2xl">
          <div className="mx-auto mb-6 flex h-24 w-24 animate-bounce items-center justify-center rounded-full bg-purple-100 text-4xl text-purple-600">
            🎉
          </div>
          <h2 className="mb-4 text-3xl font-extrabold text-gray-800">{t("all_questions_solved")}</h2>
          <p className="mb-6 text-gray-600">{t("you_have_completed_all_questions_for_this_assessment")}</p>
          <button
            onClick={() => navigate("/")}
            className="inline-flex w-full justify-center rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg hover:scale-[1.03] hover:shadow-xl"
          >
            {t("go_back_to_courses")}
          </button>
        </div>
      </div>
    );
  }

  if (submitting)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-10 py-8 shadow-2xl">
          <svg
            className="h-12 w-12 animate-spin text-purple-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>

          <p className="text-sm font-semibold text-gray-700">{t("submitting_assessment")}</p>
        </div>
      </div>
    );

  if (finished)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-tr from-gray-100 to-gray-200 px-4">
        <div className="relative flex w-full max-w-2xl flex-col justify-center overflow-hidden rounded-3xl bg-white p-10 shadow-2xl">
          <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-purple-300 opacity-60"></div>
          <div className="absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-indigo-300 opacity-30"></div>
          <div className="relative z-10 text-center">
            <h2 className="mb-6 text-3xl font-extrabold text-gray-800">{t("competition_results")}</h2>
            <div className="mb-8 flex justify-center gap-8 text-gray-700">
              <div className="rounded-lg bg-purple-50 px-6 py-4">
                <p className="text-sm text-gray-500">{t("final_score")}</p>
                <p className="mt-1 text-xl font-bold text-purple-600">
                  {score} / {total}
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-6 py-4">
                <p className="text-sm text-gray-500">{t("completion_time")}</p>
                <p className="mt-1 text-xl font-bold text-indigo-600">{completionTimeFormatted}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mx-auto rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:scale-105 hover:shadow-xl"
          >
            {i18n.language === "ar" ? "العودة إلى الرئيسية" : "Back to Home"}
          </button>
        </div>
      </div>
    );

  /* =========================
     MAIN QUESTION UI
  ========================== */
  return (
    <div className="relative mx-auto flex min-h-screen max-w-6xl px-4 py-6">
      {cameraLost && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-6">
          <div className="animate-pulse rounded-xl bg-red-100/90 px-6 py-3 text-center text-sm font-semibold text-red-700 shadow-lg backdrop-blur-sm">
            {t("camera_lost_countdown", { sec: cameraCountdown })}
          </div>
        </div>
      )}

      {/* LEFT COLUMN: QUESTIONS */}
      <div className="w-10/12">
        <div
          className={`relative w-full rounded-3xl p-6 transition-all duration-300 ${
            cameraLost ? "pointer-events-none select-none blur-sm" : ""
          }`}
        >
          <h3 className="mb-8 text-3xl font-bold text-gray-800">
            {currentQuestion?.question || t("no_question")}
          </h3>

          <div className="grid gap-4">
            {(() => {
              const opts = currentQuestion?.options || [];
              const dir = getOptionsDirection(opts);
              return opts.map((opt, idx) => (
                <button
                  key={opt.id || idx}
                  onClick={() => selectAnswer(idx)}
                  className={`flex items-center rounded-md border p-1 font-semibold shadow-sm transition-all hover:scale-[1.02] ${
                    answers[currentIndex]?.selectedIndex === idx
                      ? "border-purple-600 bg-purple-100 text-purple-700"
                      : "border-gray-300 text-gray-700"
                  }`}
                >
                  <span className="rounded-md bg-gray-100 px-6 py-4 font-bold text-purple-500 ltr:mr-4 rtl:ml-4">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span
                    dir={dir}
                    style={{ flex: 1, textAlign: dir === "ltr" ? "left" : "right" }}
                    dangerouslySetInnerHTML={{ __html: (opt.option || "").replace(/</g, "&lt;").replace(/>/g, "&gt;") }}
                  />
                </button>
              ));
            })()}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-gray-500">{t("select_answer_warning")}</p>

            {currentIndex < selectedQuestions.length - 1 ? (
              <button
                onClick={() => setCurrentIndex((prev) => prev + 1)}
                disabled={answers[currentIndex] === undefined}
                className={`rounded-md px-6 py-3 text-white ${
                  answers[currentIndex] === undefined
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {t("nextQuestion")}
              </button>
            ) : (
              <button
                onClick={finishAssessment}
                disabled={answers[currentIndex] === undefined}
                className={`rounded-lg px-6 py-3 text-white ${
                  answers[currentIndex] === undefined
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {t("Submit")}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* RIGHT COLUMN: TIMER + CAMERA */}
      <div className="flex w-1/4 flex-col gap-4">
        {/* LIVE CAMERA PREVIEW */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ boxShadow: "0px 0px 24px 1px rgba(127,127,127,0.20)" }}
          className={`w-full rounded-lg border-2 transition-all duration-300 ${
            cameraLost ? "animate-pulse border-red-600" : "border-purple-500"
          } h-36 object-cover`}
        />

        {/* TIMER */}
        <div
          className="flex flex-col items-center rounded-xl bg-white p-6 shadow-md"
          style={{ boxShadow: "0px 0px 24px 1px rgba(127,127,127,0.2)" }}
        >
          <div className="relative flex items-center justify-center">
            <svg className="h-24 w-24 -rotate-90 transform">
              <circle
                className="text-gray-200"
                strokeWidth="4"
                stroke="currentColor"
                fill="transparent"
                r="44"
                cx="48"
                cy="48"
              />
              <circle
                strokeWidth="5"
                stroke={
                  timeLeft / totalDuration > 0.5
                    ? "#16a34a"
                    : timeLeft / totalDuration > 0.2
                      ? "#eab308"
                      : "#dc2626"
                }
                strokeLinecap="round"
                fill="transparent"
                r="44"
                cx="48"
                cy="48"
                strokeDasharray={2 * Math.PI * 44}
                strokeDashoffset={2 * Math.PI * 44 * (1 - timeLeft / totalDuration)}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute text-center">
              <span
                className={`text-2xl font-semibold ${
                  timeLeft / totalDuration > 0.5
                    ? "text-green-600"
                    : timeLeft / totalDuration > 0.2
                      ? "text-yellow-500"
                      : "text-red-600"
                }`}
              >
                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
